import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { ServerTimeType } from '@op/components/FilterCommon/types';
import { DBO_ARR, DBO_EC, DBO_V } from '@op/utils/format';
import { getChartBufferWidth } from '@op/utils/systemUtil';
import * as echarts from 'echarts';
import React, { useMemo } from 'react';

export type SmallTableTimelineType = {
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  timeline: { value: number; time: string }[];
};

export default function TableMiniChart({
  data,
  name = 'CCV',
  colorMap = {
    areaStart: 'rgba(55, 106, 231, 0.3)',
    areaEnd: 'rgba(209,230,250,0.00)',
    line: '#376AE7',
  },
  height = 30,
  valueFormat = (val) => DBO_V.reviews(val),
  showTooltip = true,
  className,
  filterEmptyValue = true,
  timeFormat = 'hourly',
  tooltipModel = "one",
  ...restProps
}: {
  data?: SmallTableTimelineType;
  name?: string;
  colorMap?: {
    areaStart: string;
    areaEnd: string;
    line: string;
  };
  height?: number;
  valueFormat?: (val: number) => number | string;
  valueKey?: string;
  showTooltip?: boolean;
  className?: string;
  filterEmptyValue?: boolean;
  timeFormat?: ServerTimeType;
  tooltipModel?: "one" | "two"
}) {
  const { series, xAxis, isEmpty } = useMemo(() => {
    const series: Array<number> = [];
    const { timeline = [] } = data || {};
    if ((timeline || []).length === 0) {
      return {
        series,
        xAxis: [],
        isEmpty: true,
      };
    }

    timeline.sort(DBO_ARR.sortByTime('time', 'asc'));
    if (filterEmptyValue) {
      timeline.filter((item) => (item?.value || 0) !== 0);
    }
    const xAxis = timeline
      .map((item) => {
        series.push(item.value || 0);
        return item.time;
      });
    return {
      series,
      xAxis,
      isEmpty: series.length === 0,
    };
  }, [data]);


  const options = useMemo(() => {
    if (isEmpty) return {};
    const seriesData: any[] = [
      {
        symbol: 'circle',
        data: series,
        type: 'line',
        symbolSize: series?.length === 1 ? 5 : 0,
        lineStyle: {
          width: 2,
          color: colorMap.line,
        },
        itemStyle: {
          color: colorMap.line,
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: colorMap.areaStart,
            },
            {
              offset: 1,
              color: colorMap.areaEnd,
            },
          ]),
        },
      },
    ];

    const option = {
      tooltip: {
        show: showTooltip,
        appendToBody: showTooltip,
        position(pos: any, params: any, dom: any, rect: any, size: any) {
          return DBO_EC.position(
            pos,
            params,
            dom,
            rect,
            size,
            0,
            15,
            'bottom',
            10,
          );
        },
        confine: false,
        formatter: (param: any) => {
          const { axisValue } = param?.[0] || {};
          return formatChartParamsDate({
            params: param,
            listTitleFunc: () => name,
            valueFormat,
            tablesConfigFunc: (records: Array<any>) => {
              const timeText = listTimeTitleFunc(axisValue, timeFormat);

              return [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: tooltipModel === "one" ? name : timeText,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: tooltipModel === "one" ? timeText : "",
                  },
                ],
              },
            ]
            },
          });
        },
        ...(showTooltip ? {} : { axisPointer: { show: false } }),
      },
      legend: {
        show: false,
      },
      grid: {
        top: '5px',
        left: getChartBufferWidth(5),
        right: '5px',
        bottom: '5px',
        containLabel: false,
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: 0,
          data: xAxis,
          axisLabel: false,
          axisLine: false,
          axisTick: false,
          splitLine: false,
        },
      ],
      yAxis: {
        type: 'value',
        splitLine: false,
        axisLabel: false,
        boundaryGap: 0,
      },
      series: seriesData,
    };
    return option;
  }, [xAxis, name, isEmpty]);

  if (isEmpty) return null;

  return (
    <EChartCommon
      options={options}
      height={height}
      empty={isEmpty}
      hasEmptyImg={false}
      className={className}
      hasEmptyTitle={false}
      optionsProps={{ excludeOptions: ['axisPointer'] }}
      {...restProps}
    ></EChartCommon>
  );
}
